import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  doc, getDoc, updateDoc, increment,
  collection, addDoc, onSnapshot,
  query, orderBy, limit, serverTimestamp,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { Play, Pause, ChevronLeft, Eye, User, Share2, Music, Send, MessageCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useRadioStore } from "../stores/radioStore";

function timeAgo(ts) {
  if (!ts) return "";
  const secs = Math.floor((Date.now() - (ts.seconds ? ts.seconds * 1000 : ts)) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function ContentPage() {
  const { contentId } = useParams();
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const commentsEndRef = useRef(null);

  // Global player
  const { playContent, currentTrack, playing, togglePlay } = useRadioStore();
  const isThisContent = currentTrack?.contentId === contentId;

  useEffect(() => { loadContent(); }, [contentId]);

  const loadContent = async () => {
    try {
      const snap = await getDoc(doc(db, "content", contentId));
      if (!snap.exists()) { setLoading(false); return; }
      const data = { id: snap.id, ...snap.data() };
      setContent(data);
      updateDoc(doc(db, "content", contentId), { views: increment(1) }).catch(() => {});
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Auto-play video once content is rendered
  useEffect(() => {
    if (!content) return;
    const el = videoRef.current;
    if (el) {
      el.play().catch(() => {}); // browser may block — that's fine, controls are visible
    }
  }, [content]);

  // Real-time comments subscription
  useEffect(() => {
    if (!contentId) return;
    const q = query(
      collection(db, "content", contentId, "comments"),
      orderBy("createdAt", "asc"),
      limit(100)
    );
    const unsub = onSnapshot(q, (snap) => {
      setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      // Scroll to latest
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }, () => {});
    return unsub;
  }, [contentId]);

  // Transfer audio to global player when navigating away mid-playback
  useEffect(() => {
    return () => {
      const el = videoRef.current;
      if (el && !el.paused) {
        const { playContent: pc } = useRadioStore.getState();
        if (el._contentData) pc(el._contentData, el.currentTime);
      }
    };
  }, []);

  const postComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !user) return;
    setPosting(true);
    try {
      await addDoc(collection(db, "content", contentId, "comments"), {
        text: commentText.trim(),
        userId: user.uid,
        displayName: user.displayName || user.email?.split("@")[0] || "User",
        photoURL: user.photoURL || null,
        createdAt: serverTimestamp(),
      });
      setCommentText("");
    } catch (e) { console.error(e); }
    setPosting(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#00ff77] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!content) return (
    <div className="text-center py-20">
      <p className="text-zinc-500">Content not found.</p>
      <Link to="/" className="text-[#00ff77] text-sm mt-2 inline-block">← Go Home</Link>
    </div>
  );

  const videoURL = content.videoURL || content.videoUrl || content.url || content.fileURL || content.mediaURL || null;
  const audioURL = content.storageURL || content.audioURL || content.mp3FileUrl || content.audioUrl || null;
  const thumbURL = content.thumbnailURL || content.thumbnailUrl || content.thumbnail || null;
  const isAudio = content.type === "audio" || (!videoURL && audioURL);

  const attachRef = (el) => {
    videoRef.current = el;
    if (el) el._contentData = content;
  };

  return (
    <div data-testid="content-page" className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      <Link to="/" className="flex items-center gap-1 text-zinc-500 hover:text-white text-sm transition-colors">
        <ChevronLeft size={16} /> Back
      </Link>

      {/* Media Player */}
      <div className="rounded-2xl overflow-hidden bg-[#111] border border-white/5">
        {videoURL ? (
          <div className="relative">
            <video
              ref={attachRef}
              data-testid="content-video"
              src={videoURL}
              controls
              autoPlay
              poster={thumbURL}
              className="w-full aspect-video bg-black"
              onPlay={() => {
                useRadioStore.setState({
                  visible: true,
                  currentTrack: {
                    title: content.title,
                    artist: content.creatorName || "",
                    artworkUrl: thumbURL,
                    contentType: "video",
                    contentId: content.id,
                  },
                });
              }}
              onPause={() => {
                if (useRadioStore.getState().currentTrack?.contentId === content.id) {
                  useRadioStore.setState({ playing: false });
                }
              }}
            />
          </div>
        ) : isAudio && audioURL ? (
          <div className="p-8 flex flex-col items-center gap-5">
            {thumbURL ? (
              <img src={thumbURL} alt={content.title} className="w-40 h-40 rounded-2xl object-cover border border-white/10 shadow-lg" />
            ) : (
              <div className="w-40 h-40 rounded-2xl bg-[#1a1a1a] border border-white/10 flex items-center justify-center">
                <Music size={40} className="text-zinc-600" />
              </div>
            )}
            <button
              data-testid="play-in-player-btn"
              onClick={isThisContent ? togglePlay : () => playContent(content)}
              className="flex items-center gap-3 bg-[#00ff77] text-black font-bold px-8 py-3 rounded-full hover:bg-[#00e66b] transition-all hover:scale-105"
            >
              {isThisContent && playing ? <><Pause size={18} fill="black" /> Pause</> : <><Play size={18} fill="black" /> Play</>}
            </button>
            {isThisContent && <p className="text-[#00ff77] text-xs">Playing in footer player</p>}
          </div>
        ) : (
          <div className="aspect-video flex items-center justify-center">
            {thumbURL
              ? <img src={thumbURL} alt={content.title} className="w-full h-full object-cover" />
              : <div className="flex flex-col items-center gap-2 text-zinc-600"><Play size={48} /><p className="text-xs">No playable media</p></div>
            }
          </div>
        )}
      </div>

      {/* Title + Meta */}
      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white mb-1">{content.title}</h1>
            <div className="flex items-center gap-4 text-zinc-500 text-sm">
              <span className="flex items-center gap-1"><Eye size={13} /> {(content.views || 0) + 1} views</span>
              {content.category && <span className="bg-white/5 px-2 py-0.5 rounded-full text-xs">{content.category}</span>}
            </div>
          </div>
          <button
            data-testid="share-btn"
            onClick={() => navigator.share?.({ title: content.title, url: window.location.href })}
            className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
          >
            <Share2 size={16} />
          </button>
        </div>

        {content.creatorSlug && (
          <Link to={`/c/${content.creatorSlug}`} data-testid="content-creator-link"
            className="flex items-center gap-3 mt-4 p-3 bg-[#111] border border-white/5 rounded-xl hover:border-[#00ff77]/20 transition-colors group">
            <div className="w-10 h-10 rounded-full bg-[#1a1a1a] overflow-hidden">
              {content.creatorPhotoURL
                ? <img src={content.creatorPhotoURL} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center"><User size={16} className="text-zinc-600" /></div>}
            </div>
            <div>
              <p className="text-white text-sm font-semibold group-hover:text-[#00ff77] transition-colors">{content.creatorName}</p>
              <p className="text-zinc-500 text-xs">View Profile</p>
            </div>
          </Link>
        )}

        {content.description && (
          <div className="mt-4 bg-[#111] border border-white/5 rounded-xl p-4">
            <p className="text-zinc-300 text-sm leading-relaxed">{content.description}</p>
          </div>
        )}
      </div>

      {/* ── Comments Section ─────────────────────────── */}
      <div data-testid="comments-section" className="bg-[#111] border border-white/5 rounded-2xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
          <MessageCircle size={15} className="text-zinc-400" />
          <h3 className="text-white font-semibold text-sm">Comments</h3>
          {comments.length > 0 && (
            <span className="text-xs text-zinc-500 bg-white/5 px-2 py-0.5 rounded-full">{comments.length}</span>
          )}
        </div>

        {/* Comment list */}
        <div className="max-h-72 overflow-y-auto px-4 py-3 space-y-4">
          {comments.length === 0 ? (
            <p className="text-zinc-600 text-sm text-center py-4">No comments yet. Be the first!</p>
          ) : (
            comments.map((c) => (
              <div key={c.id} data-testid="comment-item" className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-[#1a1a1a] flex-shrink-0 overflow-hidden">
                  {c.photoURL
                    ? <img src={c.photoURL} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center">
                        <span className="text-xs font-bold text-zinc-400">{c.displayName?.[0]?.toUpperCase()}</span>
                      </div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-white text-xs font-semibold">{c.displayName}</span>
                    <span className="text-zinc-600 text-[10px]">{timeAgo(c.createdAt)}</span>
                  </div>
                  <p className="text-zinc-300 text-sm mt-0.5 break-words">{c.text}</p>
                </div>
              </div>
            ))
          )}
          <div ref={commentsEndRef} />
        </div>

        {/* Post a comment */}
        <div className="border-t border-white/5 p-4">
          {user ? (
            <form onSubmit={postComment} className="flex gap-2">
              <input
                data-testid="comment-input"
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Add a comment…"
                maxLength={300}
                className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#00ff77]/40"
              />
              <button
                type="submit"
                data-testid="post-comment-btn"
                disabled={posting || !commentText.trim()}
                className="flex items-center gap-1.5 bg-[#00ff77] text-black font-bold text-xs px-4 py-2 rounded-xl hover:bg-[#00e66b] transition-colors disabled:opacity-40"
              >
                <Send size={12} />
                {posting ? "…" : "Post"}
              </button>
            </form>
          ) : (
            <p className="text-zinc-500 text-sm text-center">
              <Link to="/auth" className="text-[#00ff77] hover:underline">Sign in</Link> to leave a comment.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
